import { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import {
  ChevronLeftIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";
import PageHeader from "../../components/layout/PageHeader";
import { Button } from "../../components/ui/Button";
import toast from "react-hot-toast";
import { campersApi } from "../../api/campers";
import { enrollmentsApi } from "../../api/enrollments";
import { termsApi } from "../../api/terms";
import { activitiesApi } from "../../api/activities";
import { completionsApi } from "../../api/completions";
import type { Camper, Enrollment, Term, Activity, Completion } from "../../types";

// ─── Types ────────────────────────────────────────────────────────────────────

interface AcCellState {
  yearInput: string;       // "" or 2-digit string e.g. "24"
  date: string;            // derived full YYYY-MM-DD or ""
  originalYearInput: string;
  originalDate: string;
}

type AcState = Record<string, AcCellState>;

// ─── Helpers ──────────────────────────────────────────────────────────────────

function computeAssumedDate(
  year: number,
  enrollments: Enrollment[],
  allTerms: Term[]
): string {
  const yearEnrollments = enrollments.filter((e) => e.year === year);
  const termIds = yearEnrollments
    .map((e) => e.termId)
    .filter((id): id is number => id != null);

  const candidates =
    termIds.length > 0
      ? allTerms.filter((t) => termIds.includes(t.id))
      : allTerms.filter((t) => t.year === year);

  const withEnd = candidates
    .filter((t) => t.endDate != null)
    .sort((a, b) => (b.endDate! > a.endDate! ? 1 : -1));

  return withEnd.length > 0 ? withEnd[0].endDate! : `${year}-12-31`;
}

function deriveDate(twoDigitYear: string, enrollments: Enrollment[], allTerms: Term[]): string {
  if (twoDigitYear.length !== 2) return "";
  const fullYear = 2000 + parseInt(twoDigitYear, 10);
  return computeAssumedDate(fullYear, enrollments, allTerms);
}

function buildAcState(activities: Activity[], completions: Completion[]): AcState {
  const completionMap = new Map(completions.map((c) => [c.achievementCreditId, c]));
  const state: AcState = {};
  for (const activity of activities) {
    if (activity.archivedAt) continue;
    for (const ac of activity.achievementCredits ?? []) {
      if (ac.archivedAt || ac.type === "graduation") continue;
      const existing = completionMap.get(ac.id);
      const completed = existing?.status === "completed";
      let yearInput = "";
      let date = "";
      if (completed && existing?.completedDate) {
        const fullYear = parseInt(existing.completedDate.slice(0, 4), 10);
        yearInput = String(fullYear % 100).padStart(2, "0");
        date = existing.completedDate;
      }
      state[String(ac.id)] = { yearInput, date, originalYearInput: yearInput, originalDate: date };
    }
  }
  return state;
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function CamperAcEntry() {
  const [allTerms, setAllTerms] = useState<Term[]>([]);
  const [activities, setActivities] = useState<Activity[]>([]);

  // Camper search
  const [camperSearch, setCamperSearch] = useState("");
  const [camperResults, setCamperResults] = useState<Camper[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [selectedCamper, setSelectedCamper] = useState<Camper | null>(null);
  const searchRef = useRef<HTMLDivElement>(null);

  const [enrollments, setEnrollments] = useState<Enrollment[]>([]);
  const [acState, setAcState] = useState<AcState>({});
  const [dataLoading, setDataLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [fillYear, setFillYear] = useState("");

  // Load static data on mount
  useEffect(() => {
    Promise.all([termsApi.list(), activitiesApi.list()])
      .then(([terms, acts]) => {
        setAllTerms(terms);
        setActivities(acts);
      })
      .catch(() => toast.error("Failed to load data"));
  }, []);

  // Camper search debounce
  useEffect(() => {
    if (camperSearch.trim().length < 2) {
      setCamperResults([]);
      setShowResults(false);
      return;
    }
    const timer = setTimeout(() => {
      let cancelled = false;
      setSearchLoading(true);
      campersApi
        .list({ search: camperSearch.trim(), pageSize: 15 })
        .then((res) => {
          if (!cancelled) { setCamperResults(res.data); setShowResults(true); }
        })
        .catch(() => { if (!cancelled) toast.error("Failed to search campers"); })
        .finally(() => { if (!cancelled) setSearchLoading(false); });
      return () => { cancelled = true; };
    }, 300);
    return () => clearTimeout(timer);
  }, [camperSearch]);

  // Dismiss dropdown on outside click
  useEffect(() => {
    function onDown(e: MouseEvent) {
      if (searchRef.current && !searchRef.current.contains(e.target as Node))
        setShowResults(false);
    }
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  // Select a camper: load enrollments + completions together
  const handleCamperSelect = useCallback(
    (camper: Camper) => {
      setSelectedCamper(camper);
      setCamperSearch("");
      setCamperResults([]);
      setShowResults(false);
      setAcState({});
      setEnrollments([]);
      setDataLoading(true);
      Promise.all([
        enrollmentsApi.list({ camperId: camper.id }),
        completionsApi.list({ camperId: camper.id }),
      ])
        .then(([enrs, completions]) => {
          setEnrollments(enrs);
          setAcState(buildAcState(activities, completions));
        })
        .catch(() => toast.error("Failed to load camper data"))
        .finally(() => setDataLoading(false));
    },
    [activities]
  );

  function handleClearCamper() {
    setSelectedCamper(null);
    setEnrollments([]);
    setAcState({});
    setFillYear("");
  }

  // Handle typing a year into a cell
  const handleYearInput = useCallback(
    (acId: string, rawValue: string) => {
      const digits = rawValue.replace(/\D/g, "").slice(0, 2);
      const date = deriveDate(digits, enrollments, allTerms);
      setAcState((prev) => {
        const row = prev[acId];
        if (!row) return prev;
        return { ...prev, [acId]: { ...row, yearInput: digits, date } };
      });
    },
    [enrollments, allTerms]
  );

  // Stamp a single cell: toggle if already stamped, apply otherwise
  const handleStamp = useCallback(
    (acId: string) => {
      const digits = fillYear.replace(/\D/g, "").slice(0, 2);
      if (digits.length !== 2) return;
      setAcState((prev) => {
        const row = prev[acId];
        if (!row) return prev;
        if (row.yearInput === digits) {
          // Already stamped with this year — clear it
          return { ...prev, [acId]: { ...row, yearInput: "", date: "" } };
        }
        const date = deriveDate(digits, enrollments, allTerms);
        return { ...prev, [acId]: { ...row, yearInput: digits, date } };
      });
    },
    [fillYear, enrollments, allTerms]
  );

  // Dirty: only count cells with a complete (2-digit) or cleared change
  const dirtyCount = Object.values(acState).filter((r) => {
    if (r.yearInput === r.originalYearInput && r.date === r.originalDate) return false;
    // Ignore partial/in-progress (1 digit typed but not yet complete)
    if (r.yearInput.length === 1) return false;
    return true;
  }).length;

  async function handleSave() {
    if (!selectedCamper) return;

    const completions: {
      camperId: number;
      achievementCreditId: number;
      status: string;
      completedDate?: string;
    }[] = [];

    for (const [acIdStr, row] of Object.entries(acState)) {
      if (row.yearInput.length === 1) continue; // skip in-progress
      const changed = row.yearInput !== row.originalYearInput || row.date !== row.originalDate;
      if (!changed) continue;
      const achievementCreditId = Number(acIdStr);
      if (row.yearInput.length === 2 && row.date) {
        completions.push({ camperId: selectedCamper.id, achievementCreditId, status: "completed", completedDate: row.date });
      } else if (!row.yearInput && row.originalYearInput) {
        completions.push({ camperId: selectedCamper.id, achievementCreditId, status: "pending" });
      }
    }

    if (completions.length === 0) { toast("No changes to save."); return; }

    setSaving(true);
    try {
      await completionsApi.bulkCreate(completions);
      setAcState((prev) => {
        const next = { ...prev };
        for (const k of Object.keys(next))
          next[k] = { ...next[k], originalYearInput: next[k].yearInput, originalDate: next[k].date };
        return next;
      });
      toast.success(`Saved ${completions.length} record${completions.length !== 1 ? "s" : ""}`);
    } catch {
      toast.error("Failed to save changes");
    } finally {
      setSaving(false);
    }
  }

  const visibleActivities = activities
    .filter((a) => !a.archivedAt)
    .map((a) => ({
      ...a,
      achievementCredits: (a.achievementCredits ?? []).filter(
        (ac) => !ac.archivedAt && ac.type !== "graduation"
      ),
    }))
    .filter((a) => a.achievementCredits.length > 0)
    .sort((a, b) => a.name.localeCompare(b.name));

  const maxAcs = visibleActivities.length > 0
    ? Math.max(...visibleActivities.map((a) => a.achievementCredits.length))
    : 0;

  return (
    <div>
      <PageHeader
        title="Camper AC Entry"
        subtitle="Enter year (2 digits) per AC — tab across rows to move quickly"
        actions={
          selectedCamper && !dataLoading && dirtyCount > 0 ? (
            <Button variant="primary" loading={saving} onClick={handleSave}>
              Save Changes ({dirtyCount})
            </Button>
          ) : undefined
        }
      />

      <div className="px-6 lg:px-8 pt-4 pb-2 space-y-4">
        {/* Breadcrumb */}
        <Link
          to="/admin/manage-acs"
          className="inline-flex items-center gap-1.5 text-sm text-stone-500 hover:text-stone-800"
        >
          <ChevronLeftIcon className="w-4 h-4" />
          Back to Manage ACs
        </Link>

        {/* Controls */}
        <div className="flex flex-wrap items-end gap-5">
          {/* Camper */}
          <div>
            <label className="block text-xs font-medium text-stone-500 uppercase tracking-wide mb-1.5">
              Camper
            </label>
            {selectedCamper ? (
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-stone-900">
                  {selectedCamper.firstName} {selectedCamper.lastName}
                </span>
                <button
                  type="button"
                  onClick={handleClearCamper}
                  className="text-stone-400 hover:text-stone-600"
                  title="Change camper"
                >
                  <XMarkIcon className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div ref={searchRef} className="relative w-52">
                <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400 pointer-events-none" />
                <input
                  type="text"
                  placeholder="Search by name…"
                  value={camperSearch}
                  onChange={(e) => setCamperSearch(e.target.value)}
                  onFocus={() => camperResults.length > 0 && setShowResults(true)}
                  className="w-full pl-8 pr-3 py-1.5 rounded-md border border-stone-300 text-sm focus:outline-none focus:ring-2 focus:ring-forest-500"
                />
                {searchLoading && (
                  <div className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 border-2 border-forest-500 border-t-transparent rounded-full animate-spin" />
                )}
                {showResults && camperResults.length > 0 && (
                  <ul className="absolute z-50 mt-1 w-full bg-white border border-stone-200 rounded-md shadow-lg max-h-52 overflow-y-auto">
                    {camperResults.map((c) => (
                      <li key={c.id}>
                        <button
                          type="button"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => handleCamperSelect(c)}
                          className="w-full text-left px-3 py-2 text-sm text-stone-800 hover:bg-stone-50"
                        >
                          {c.firstName} {c.lastName}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                {showResults && !searchLoading && camperResults.length === 0 && (
                  <div className="absolute z-50 mt-1 w-full bg-white border border-stone-200 rounded-md shadow-lg px-3 py-2 text-sm text-stone-400">
                    No campers found.
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Stamp year */}
          {selectedCamper && !dataLoading && (
            <div className="flex items-end gap-2">
              <div>
                <label className="block text-xs font-medium text-stone-500 uppercase tracking-wide mb-1.5">
                  Stamp year
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={2}
                  placeholder="YY"
                  value={fillYear}
                  onChange={(e) => setFillYear(e.target.value.replace(/\D/g, "").slice(0, 2))}
                  className={`w-12 text-center rounded-md border text-sm px-1 py-1.5 focus:outline-none focus:ring-2 focus:ring-forest-500 ${
                    fillYear.length === 2
                      ? "border-forest-400 bg-forest-50 text-forest-800 font-semibold"
                      : "border-stone-300"
                  }`}
                />
              </div>
              {fillYear.length === 2 && (
                <span className="flex items-center gap-1 text-xs text-forest-700 font-medium pb-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-forest-500 inline-block" />
                  Stamp {fillYear} active — click cells to apply
                </span>
              )}
            </div>
          )}

          {/* Dirty count hint */}
          {dirtyCount > 0 && (
            <p className="text-sm text-stone-500 pb-0.5">
              {dirtyCount} unsaved change{dirtyCount !== 1 ? "s" : ""}
            </p>
          )}
        </div>
      </div>

      {/* AC Table — own scroll container so sticky header works */}
      {selectedCamper && (
        <div className="px-6 lg:px-8 pb-6">
          {dataLoading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-6 h-6 border-2 border-forest-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : visibleActivities.length === 0 ? (
            <div className="text-center py-20 text-stone-400 text-sm">
              No activities with Achievement Credits found.
            </div>
          ) : (
            <>
              <div className="overflow-auto max-h-[calc(100vh-260px)] rounded-xl border border-stone-200 shadow-sm">
                <table className="border-collapse min-w-full text-sm">
                  <thead>
                    <tr>
                      <th className="sticky top-0 left-0 z-30 bg-stone-100 border-b border-r border-stone-200 px-4 py-2 text-left text-xs font-semibold text-stone-500 uppercase tracking-wide min-w-[160px] whitespace-nowrap">
                        Activity
                      </th>
                      {Array.from({ length: maxAcs }, (_, i) => (
                        <th
                          key={i}
                          className="sticky top-0 z-20 bg-stone-100 border-b border-stone-200 border-l border-stone-150 py-2 text-center text-xs font-semibold text-stone-500 w-10 min-w-[2.5rem]"
                        >
                          {i + 1}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-stone-100">
                    {visibleActivities.map((activity) => (
                      <tr key={activity.id} className="group hover:bg-stone-50">
                        <td className="sticky left-0 z-10 bg-white group-hover:bg-stone-50 border-r border-stone-200 px-4 py-1.5 font-medium text-stone-800 whitespace-nowrap">
                          {activity.name}
                        </td>
                        {Array.from({ length: maxAcs }, (_, i) => {
                          const ac = activity.achievementCredits[i];
                          if (!ac) {
                            return <td key={i} className="w-10 min-w-[2.5rem] bg-stone-50/60 border-l border-stone-100" />;
                          }
                          const acId = String(ac.id);
                          const row = acState[acId];
                          const filled = row?.yearInput?.length === 2;
                          const partial = row?.yearInput?.length === 1;
                          const stampMode = fillYear.length === 2;
                          const isStamped = stampMode && row?.yearInput === fillYear;
                          return (
                            <td
                              key={i}
                              className={`px-1 py-1 text-center border-l border-stone-100 w-10 min-w-[2.5rem] transition-colors ${
                                stampMode ? "cursor-pointer hover:bg-forest-50" : ""
                              } ${isStamped ? "bg-forest-50/40" : ""}`}
                              title={`#${ac.displayNumber} — ${ac.name}`}
                              onClick={stampMode ? () => handleStamp(acId) : undefined}
                            >
                              <input
                                type="text"
                                inputMode="numeric"
                                maxLength={2}
                                value={row?.yearInput ?? ""}
                                readOnly={stampMode}
                                onChange={stampMode ? undefined : (e) => handleYearInput(acId, e.target.value)}
                                placeholder="--"
                                className={`w-8 text-center text-xs rounded border py-0.5 transition-colors ${
                                  stampMode
                                    ? "pointer-events-none cursor-pointer focus:outline-none"
                                    : "focus:outline-none focus:ring-1 focus:ring-forest-500"
                                } ${
                                  filled
                                    ? "border-forest-400 bg-forest-50 text-forest-800 font-semibold"
                                    : partial
                                    ? "border-amber-300 bg-amber-50 text-amber-800"
                                    : "border-stone-200 text-stone-400 bg-transparent placeholder:text-stone-300"
                                }`}
                              />
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {dirtyCount > 0 && (
                <div className="flex justify-end pt-3">
                  <Button variant="primary" loading={saving} onClick={handleSave}>
                    Save Changes ({dirtyCount})
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
